import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ComputerRoundedIcon from "@mui/icons-material/ComputerRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { createConnector, listConnectors } from "../api/connectors";
import { getApiError } from "../api/errors";
import PageHeader from "../components/PageHeader";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, titleCase } from "../utils/format";

const CONNECTOR_CACHE_PREFIX = "abos_connector_status";

function normalizeConnectorList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.connectors)) return payload.connectors;
  return [];
}

function connectorCacheKey(workspaceId) {
  return `${CONNECTOR_CACHE_PREFIX}:${workspaceId || "none"}`;
}

function readConnectorCache(workspaceId) {
  if (!workspaceId) return [];

  try {
    const parsed = JSON.parse(
      sessionStorage.getItem(connectorCacheKey(workspaceId)) || "null",
    );
    return normalizeConnectorList(parsed?.items);
  } catch {
    return [];
  }
}

function writeConnectorCache(workspaceId, connectors) {
  if (!workspaceId) return;

  try {
    sessionStorage.setItem(
      connectorCacheKey(workspaceId),
      JSON.stringify({ items: connectors, cached_at: new Date().toISOString() }),
    );
  } catch {
    // The live response remains authoritative when browser storage is unavailable.
  }
}

function connectorStoreId(connector) {
  return connector?.store_id
    || connector?.store_public_id
    || connector?.store?.public_id
    || "";
}

function statusMeta(status) {
  switch (status) {
    case "active":
      return {
        label: "Connected",
        color: "success",
        message: "Automatic read-only POS synchronization is enabled.",
      };
    case "disabled":
      return {
        label: "Paused by support",
        color: "default",
        message: "Platform operations temporarily disabled synchronization.",
      };
    case "revoked":
      return {
        label: "Revoked",
        color: "error",
        message: "These connector credentials can no longer access the SaaS.",
      };
    case "expired":
      return {
        label: "Activation expired",
        color: "warning",
        message: "The one-time activation code expired before installation.",
      };
    case "pending":
    default:
      return {
        label: "Awaiting activation",
        color: "warning",
        message: "Enter the one-time code in the Windows connector within ten minutes.",
      };
  }
}

export default function POSIntegrations() {
  const { selectedStore, selectedWorkspace } = useAuth();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadState, setLoadState] = useState("loading");
  const [showingCachedStatus, setShowingCachedStatus] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [activation, setActivation] = useState(null);
  const loadInFlightRef = useRef("");
  const requestSequenceRef = useRef(0);

  const load = useCallback(async ({ silent = false } = {}) => {
    const workspaceId = selectedWorkspace?.public_id;
    if (!workspaceId || loadInFlightRef.current === workspaceId) return;

    const requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;
    loadInFlightRef.current = workspaceId;

    if (!silent) {
      const cached = readConnectorCache(workspaceId);
      if (cached.length) {
        setConnectors(cached);
        setShowingCachedStatus(true);
        setLoading(false);
      } else {
        setLoading(true);
      }
    } else {
      setRefreshing(true);
    }

    try {
      const result = await listConnectors();
      if (requestId !== requestSequenceRef.current) return;

      const nextConnectors = normalizeConnectorList(result);
      setConnectors(nextConnectors);
      writeConnectorCache(workspaceId, nextConnectors);
      setLoadState("ready");
      setShowingCachedStatus(false);
      setError("");
    } catch (requestError) {
      if (requestId !== requestSequenceRef.current) return;

      const cached = readConnectorCache(workspaceId);
      if (cached.length) {
        setConnectors(cached);
        setShowingCachedStatus(true);
        setError(
          "Live connector status is temporarily unavailable. Showing the last successful status.",
        );
      } else {
        setError(getApiError(requestError, "Could not load connector status."));
      }
      setLoadState("error");
    } finally {
      if (requestId !== requestSequenceRef.current) return;

      setLoading(false);
      setRefreshing(false);
      loadInFlightRef.current = "";
    }
  }, [selectedWorkspace?.public_id]);

  useEffect(() => {
    requestSequenceRef.current += 1;
    loadInFlightRef.current = "";
    const cached = readConnectorCache(selectedWorkspace?.public_id);
    setConnectors(cached);
    setShowingCachedStatus(cached.length > 0);
    setLoadState(cached.length ? "stale" : "loading");
    setError("");
    load();
    const interval = window.setInterval(() => load({ silent: true }), 15000);
    return () => window.clearInterval(interval);
  }, [load, selectedWorkspace?.public_id]);

  const selectedStoreConnectors = useMemo(
    () => connectors.filter(
      (connector) => String(connectorStoreId(connector)).trim()
        === String(selectedStore?.public_id || "").trim(),
    ),
    [connectors, selectedStore?.public_id],
  );

  const currentConnector = selectedStoreConnectors.find(
    (connector) => connector.status !== "revoked",
  );

  const openCreate = () => {
    setName(`${selectedStore?.name || "Store"} Connector`);
    setActivation(null);
    setError("");
    setDialogOpen(true);
  };

  const create = async () => {
    if (name.trim().length < 2) {
      setError("Enter a recognizable connector name.");
      return;
    }

    setCreating(true);
    setError("");
    try {
      const result = await createConnector({
        name: name.trim(),
        store_public_id: selectedStore.public_id,
      });
      setActivation(result);
      setToast("Connector created. Copy the activation code now.");
      await load({ silent: true });
    } catch (requestError) {
      setError(getApiError(requestError, "Could not create the connector."));
    } finally {
      setCreating(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(activation.activation_code);
      setToast("Activation code copied.");
    } catch (copyError) {
      setToast("Could not copy automatically. Select the code manually.");
    }
  };

  return (
    <MainLayout>
      <PageHeader
        eyebrow="Integrations"
        title="POS connector"
        description={`Keep ${selectedStore?.name || "this store"} synchronized with its local POS using the secure read-only Windows connector.`}
        actions={(
          <>
            <Tooltip title="Refresh connection status">
              <span>
                <IconButton
                  disabled={refreshing}
                  onClick={() => load({ silent: true })}
                  sx={{ bgcolor: "white", border: "1px solid #e2e8f0" }}
                >
                  {refreshing ? <CircularProgress size={20} /> : <RefreshRoundedIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openCreate}
              disabled={Boolean(currentConnector) || loadState !== "ready"}
            >
              Set up connector
            </Button>
          </>
        )}
      />

      {error && (
        <Alert
          severity={connectors.length ? "warning" : "error"}
          sx={{ mb: 3, borderRadius: 3 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {showingCachedStatus && !error && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          Showing the most recently loaded connector status while the live status refreshes.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        You can create and monitor the connector here. For safety, pausing, replacing, or revoking a connector is handled by support.
      </Alert>

      {currentConnector && (
        <Alert
          severity={currentConnector.status === "active" ? "success" : "warning"}
          sx={{ mb: 3, borderRadius: 3 }}
        >
          This store already has a connector. Contact support when a replacement, credential revocation, or reactivation is required.
        </Alert>
      )}

      <Card sx={{ mb: 3.5, borderRadius: 4, border: "1px solid #dbeafe", bgcolor: "#f8fbff" }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    bgcolor: "#dbeafe",
                    color: "#1d4ed8",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  🔐
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={900}>
                    Secure activation flow
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    Activation codes are shown once, expire after ten minutes, and are exchanged for an encrypted machine credential. The connector remains read-only against the store POS.
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                justifyContent={{ md: "flex-end" }}
              >
                <Chip label="Read-only POS" variant="outlined" sx={{ bgcolor: "white", fontWeight: 700 }} />
                <Chip label="Tenant bound" variant="outlined" sx={{ bgcolor: "white", fontWeight: 700 }} />
                <Chip label="Platform managed" variant="outlined" sx={{ bgcolor: "white", fontWeight: 700 }} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && connectors.length === 0 ? (
        <LoadingPanel label="Loading connectors…" />
      ) : loadState === "error" && selectedStoreConnectors.length === 0 ? (
        <EmptyPanel
          icon="🔌"
          title="Connector status unavailable"
          description="The connector may still be synchronizing normally, but the dashboard could not verify its live status. Retry after checking the browser-to-API connection."
          action={(
            <Button variant="contained" onClick={() => load()} disabled={loading || refreshing}>
              Retry status
            </Button>
          )}
        />
      ) : selectedStoreConnectors.length === 0 ? (
        <EmptyPanel
          icon="🔌"
          title="No connector history for this store"
          description="Create a connector, install the latest Windows connector on the POS computer, and enter the one-time activation code."
          action={<Button variant="contained" onClick={openCreate}>Create connector</Button>}
        />
      ) : (
        <Stack spacing={2.25}>
          {selectedStoreConnectors.map((connector) => {
            const meta = statusMeta(connector.status);
            return (
              <Card
                key={connector.connector_id}
                sx={{
                  borderRadius: 4,
                  border: connector.status === "revoked"
                    ? "1px solid #fecaca"
                    : "1px solid #e2e8f0",
                  boxShadow: "0 14px 35px rgba(15,23,42,.05)",
                  opacity: connector.status === "revoked" ? 0.8 : 1,
                }}
              >
                <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    gap={2.5}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 54,
                          height: 54,
                          borderRadius: 3,
                          bgcolor: connector.status === "active" ? "#dcfce7" : "#f1f5f9",
                          color: connector.status === "active" ? "#15803d" : "#475569",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        {connector.status === "active"
                          ? <CheckCircleRoundedIcon />
                          : <ComputerRoundedIcon />}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight={900}
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {connector.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={meta.label}
                            color={meta.color}
                            sx={{ fontWeight: 800 }}
                          />
                        </Stack>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                          {meta.message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Last heartbeat: {formatDate(connector.last_seen_at)}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                          <Chip
                            size="small"
                            label={connector.capabilities?.inventory === false ? "Inventory unavailable" : "Inventory sync"}
                            color={connector.capabilities?.inventory === false ? "default" : "success"}
                            variant="outlined"
                            sx={{ fontWeight: 750 }}
                          />
                          <Chip
                            size="small"
                            label={connector.capabilities?.sales ? "Sales reports available" : "Sales data unavailable"}
                            color={connector.capabilities?.sales ? "success" : "default"}
                            variant="outlined"
                            sx={{ fontWeight: 750 }}
                          />
                          {connector.capabilities?.cost_prices && (
                            <Chip
                              size="small"
                              label="Profit reporting available"
                              color="success"
                              variant="outlined"
                              sx={{ fontWeight: 750 }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>

                    <Chip
                      label="Managed by support"
                      variant="outlined"
                      sx={{ alignSelf: { xs: "flex-start", md: "center" }, fontWeight: 800 }}
                    />
                  </Stack>

                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      mt: 2,
                      borderTop: "1px solid #e2e8f0",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0 }}>
                      <Typography variant="body2" fontWeight={800}>
                        Installation details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Device</Typography>
                          <Typography fontWeight={700}>{connector.device_name || "Not activated"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Version</Typography>
                          <Typography fontWeight={700}>{connector.version || "—"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Created</Typography>
                          <Typography variant="body2">{formatDate(connector.created_at)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Activated</Typography>
                          <Typography variant="body2">{formatDate(connector.activated_at)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Typography variant="body2">{titleCase(connector.status)}</Typography>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !creating && setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle fontWeight={900}>
          {activation ? "Activate the POS computer" : "Create store connector"}
        </DialogTitle>
        <DialogContent>
          {activation ? (
            <>
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 3 }}>
                Copy this code now. It is returned only once and expires at {formatDate(activation.activation_expires_at)}.
              </Alert>
              <Box
                sx={{
                  p: 2.5,
                  border: "1px dashed #64748b",
                  borderRadius: 3,
                  bgcolor: "#f8fafc",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  fontFamily="monospace"
                  fontWeight={900}
                  fontSize={{ xs: "1.05rem", sm: "1.45rem" }}
                  sx={{ letterSpacing: 1, overflowWrap: "anywhere" }}
                >
                  {activation.activation_code}
                </Typography>
                <Tooltip title="Copy activation code">
                  <IconButton onClick={copyCode}>
                    <ContentCopyRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Open AI Store Connector 1.0.4 on the POS computer, set the backend URL, and enter this code.
              </Typography>
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
                This connector will be permanently bound to {selectedStore?.name}.
              </Alert>
              <TextField
                autoFocus
                fullWidth
                label="Connector name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                inputProps={{ maxLength: 120 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            {activation ? "Done" : "Cancel"}
          </Button>
          {!activation && (
            <Button variant="contained" onClick={create} disabled={creating}>
              {creating
                ? <CircularProgress size={21} color="inherit" />
                : "Create activation code"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        message={toast}
      />
    </MainLayout>
  );
}
